/**
 * Device Wi-Fi hot-switch — Live API client (Firmware-F2 ops plane).
 * Production: PG job model only · no invented success · credentials never stored.
 */
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";

const INFLIGHT = new Set(["dispatching", "dispatched"]);

/** Hosted ESP32-C6 STA is 2.4 GHz only — honest scan/connect cap (Soft≠Live). */
export const WIFI_C6_SCAN_RADIO_NOTE =
  "ESP32-C6 hosted radio: 2.4 GHz scan and connect only · 5 GHz SSIDs are not visible and cannot be joined.";

const WIFI_5G_SSID_RE = /(?:^|[^0-9])5\s*g(?:hz)?(?:[^0-9]|$)|[_\-. ]5g(?:hz)?(?:[_\-. ]|$)/i;

/** Pure: band usability hint for a scanned SSID label. */
export function wifiScanBandHintForSsid(ssid) {
  const id = String(ssid || "").trim();
  if (!id) return null;
  if (WIFI_5G_SSID_RE.test(id)) {
    return {
      usable: false,
      hint: "5 GHz · not usable on C6 (2.4 GHz STA only)",
    };
  }
  return {
    usable: true,
    hint: "2.4 GHz · device can join",
  };
}

function mapScanNetwork(n) {
  const id = String((n && n.ssid) || "").trim();
  const band = wifiScanBandHintForSsid(id);
  return {
    id,
    label: id,
    rssi: n && n.rssi != null && Number.isFinite(Number(n.rssi)) ? Number(n.rssi) : null,
    bandUsable: band ? band.usable : null,
    bandHint: band ? band.hint : null,
  };
}

export function wifiOpsIdempotencyKey(action, deviceId) {
  const prefix = ["wifi", String(action || "op"), String(deviceId || "")].join(":");
  const nonce = globalThis.crypto && typeof globalThis.crypto.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : String(Date.now()) + ":" + Math.random().toString(36).slice(2);
  return prefix + ":" + nonce;
}

export function wifiOpsPath(op, deviceId, jobId = "") {
  const did = encodeURIComponent(String(deviceId || "").trim());
  if (!did) return "";
  try {
    return path(op, jobId ? { pathParams: [deviceId, jobId] } : { pathParams: [deviceId] });
  } catch {
    if (op === "get__devices_p_wifi-ops_status") return "/devices/" + did + "/wifi-ops/status";
    if (op === "get__devices_p_wifi-ops_jobs_p") return "/devices/" + did + "/wifi-ops/jobs/" + encodeURIComponent(jobId);
    if (op === "post__devices_p_wifi-ops_stage") return "/devices/" + did + "/wifi-ops/stage";
    if (op === "post__devices_p_wifi-ops_scan") return "/devices/" + did + "/wifi-ops/scan";
    if (op === "post__devices_p_wifi-ops_switch") return "/devices/" + did + "/wifi-ops/switch";
    if (op === "post__devices_p_wifi-ops_clear") return "/devices/" + did + "/wifi-ops/clear";
    return "";
  }
}

export function wifiOpsStatusViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : null;
  if (!root) return null;
  const proj = root.projection && typeof root.projection === "object" ? root.projection : {};
  const inflight = root.inflight_job && typeof root.inflight_job === "object" ? root.inflight_job : null;
  const nets = Array.isArray(proj.scan_networks) ? proj.scan_networks : [];
  return {
    stagedSsid: String(proj.staged_ssid || "").trim(),
    liveSsid: String(proj.live_ssid || "").trim(),
    linkKind: String(proj.link_kind || "unknown").trim(),
    dualLink: proj.dual_link === true,
    wifiUp: proj.wifi_up,
    ethUp: proj.eth_up,
    switchConfirmed: proj.switch_confirmed === true,
    scanRadioNote: WIFI_C6_SCAN_RADIO_NOTE,
    scanNetworks: nets.map(mapScanNetwork).filter((r) => r.id),
    inflight,
    telemetryAt: proj.telemetry_observed_at || null,
    raw: root,
  };
}

export function wifiJobViewOf(job) {
  if (!job || typeof job !== "object") return null;
  const state = String(job.state || "").trim();
  return {
    jobId: String(job.job_id || "").trim(),
    action: String(job.action || "").trim(),
    state,
    accepted: INFLIGHT.has(state) || job.accepted === true,
    completed: job.completed === true || state === "acked_ok" || state === "acked_fail" || state === "dispatch_failed",
    success: job.success === true || (state === "acked_ok" && job.ack_ok === true),
    ackOk: job.ack_ok,
    ackDetail: String(job.ack_detail || "").trim(),
    requestMeta: job.request_meta && typeof job.request_meta === "object" ? job.request_meta : {},
    result: job.result && typeof job.result === "object" ? job.result : {},
  };
}

/** Switched = ACK success + telemetry confirms SSID (never localhost-invented). */
export function wifiSwitchedSay(statusView, jobView) {
  if (!statusView || isLocalHost()) return { tone: "dim", say: "Local preview · not switched on board" };
  if (statusView.switchConfirmed) return { tone: "go", say: "Switched · device telemetry confirms SSID" };
  const j = jobView || (statusView.inflight ? wifiJobViewOf(statusView.inflight) : null);
  if (j && j.accepted) return { tone: "wait", say: "Job accepted · waiting for device ACK" };
  if (j && j.completed && !j.success) return { tone: "bad", say: j.ackDetail || "Switch failed" };
  return { tone: "dim", say: "Not switched" };
}

export function scanHitsFromJob(jobView, statusView) {
  const fromJob = jobView && jobView.result && Array.isArray(jobView.result.networks)
    ? jobView.result.networks : [];
  if (fromJob.length) {
    return fromJob.map((n) => {
      const row = mapScanNetwork(n);
      const rssiSay = row.rssi != null ? ("RSSI " + row.rssi) : "Device scan hit";
      return {
        ...row,
        say: row.bandHint ? (rssiSay + " · " + row.bandHint) : rssiSay,
        match: false,
      };
    }).filter((h) => h.id);
  }
  return (statusView && statusView.scanNetworks || []).map((n) => {
    const rssiSay = n.rssi != null ? ("RSSI " + n.rssi + " · telemetry") : "Telemetry scan hit";
    return {
      id: n.id,
      label: n.label,
      rssi: n.rssi,
      bandUsable: n.bandUsable,
      bandHint: n.bandHint,
      say: n.bandHint ? (rssiSay + " · " + n.bandHint) : rssiSay,
      match: false,
    };
  });
}

export async function wifiOpsPollJob(act, deviceId, jobId, { intervalMs = 2500, maxTries = 48 } = {}) {
  const did = String(deviceId || "").trim();
  const jid = String(jobId || "").trim();
  if (!did || !jid) return null;
  const url = wifiOpsPath("get__devices_p_wifi-ops_jobs_p", did, jid);
  for (let i = 0; i < maxTries; i += 1) {
    const out = await act.run(url, { method: "GET" });
    const job = out && out.job ? wifiJobViewOf(out.job) : null;
    if (job && job.completed) return job;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function useWifiOpsStatus(deviceId) {
  const did = String(deviceId || "").trim();
  return useApi(did ? wifiOpsPath("get__devices_p_wifi-ops_status", did) : "");
}
